import React, { useEffect, useRef, useState } from "react";

const PURPLE = "#6B43B5";
const reduceMotion = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Right-docked host panel: avatar + the current step's message (typed out) +
// progress + controls. The single advancing surface (Next/Back/Skip/Exit).
// Accessible: role=dialog, labelled/described, keyboard Esc/Enter/arrows, focus.
const ChatPanel = ({
  step, index, total, isJit,
  onNext, onBack, onSkip, onExit, canBack, isLast,
  hostName = "Smilepass guide",
}) => {
  const [typed, setTyped] = useState("");
  const panelRef = useRef(null);
  const body = (step && step.body) || "";

  // Typewriter (respect reduced motion).
  useEffect(() => {
    if (!body) { setTyped(""); return undefined; }
    if (reduceMotion()) { setTyped(body); return undefined; }
    setTyped("");
    let i = 0;
    const id = setInterval(() => {
      i += 2;
      setTyped(body.slice(0, i));
      if (i >= body.length) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [body]);

  // Move focus to the panel when the step changes (a11y).
  useEffect(() => {
    if (panelRef.current) panelRef.current.focus();
  }, [step && step.id]);

  const revealAll = () => setTyped(body);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onExit(); }
    else if (e.key === "Enter" || e.key === "ArrowRight") { e.preventDefault(); typed.length < body.length ? revealAll() : onNext(); }
    else if (e.key === "ArrowLeft" && canBack) { e.preventDefault(); onBack(); }
  };

  if (!step) return null;

  return (
    <div
      className="go-chat-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="go-step-title"
      aria-describedby="go-step-body"
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <div className="go-chat-header">
        <div className="go-avatar" aria-hidden="true">SP</div>
        <div className="go-host-name">{hostName}</div>
        <button className="go-exit" aria-label="Exit tour" onClick={onExit}>✕</button>
      </div>

      {/* Top half: the step copy, progress, and the Back/Skip/Next controls
          sit at the mid-point of the panel. */}
      <div className="go-chat-top">
        <div className="go-chat-body">
          <div id="go-step-title" className="go-step-title">{step.title}</div>
          <div id="go-step-body" className="go-step-body" aria-live="polite" onClick={revealAll}>
            {typed}
            {typed.length < body.length ? <span className="go-cursor" aria-hidden="true">▌</span> : null}
          </div>
        </div>

        {!isJit ? (
          <div className="go-progress" aria-hidden="true">
            <div className="go-progress-track"><div className="go-progress-fill" style={{ width: `${total ? ((index + 1) / total) * 100 : 0}%` }} /></div>
            <span className="go-progress-text">{index + 1} / {total}</span>
          </div>
        ) : null}

        <div className="go-chat-controls">
          {!isJit && canBack ? (
            <button className="go-btn go-btn-ghost" onClick={onBack}>Back</button>
          ) : <span />}
          <div className="go-controls-right">
            {!isJit ? <button className="go-btn go-btn-link" onClick={onSkip}>Skip tour</button> : null}
            <button className="go-btn go-btn-primary" style={{ background: PURPLE, borderColor: PURPLE }} onClick={onNext}>
              {isJit ? "Got it" : isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom half: an educational "Learn" card when the step carries one
          (FTC training tour), else the support placeholder (clinic tour). */}
      {step.learn ? (
        <div className="go-chat-learn">
          <div className="go-learn-card">
            <div className="go-learn-title">📚 {step.learnTitle || "Learn"}</div>
            <div className="go-learn-body">
              {Array.isArray(step.learn)
                ? step.learn.map((p, i) => <p key={i}>{p}</p>)
                : step.learn}
            </div>
          </div>
        </div>
      ) : (
        <div className="go-chat-support">
          <div className="go-support-card">
            <div className="go-support-title">Need a hand?</div>
            <div className="go-support-text">
              Connect with a Smilepass specialist for live help getting your office set up.
            </div>
            <button type="button" className="go-btn go-support-btn">
              Connect to a support agent
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
